import { useState, useEffect } from "react";
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Box,
  useTheme,
  Stack,
  Button,
  Link,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { motion, useAnimation } from "framer-motion";
import fetcher from "../../../api/fetcher";
import { END_POINTS } from "../../../api/EndPoints";
import useFirebaseNotification from "../../../hooks/useFirebaseNotification";
import { onMessage } from "firebase/messaging";
import { messaging } from "../../../firebase/firebase";
import { showToast } from "../../../utils/Toast";
import { fDateTime } from "../../../utils/formatTime";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "../../../routes/paths";
import { useAuthContext } from "../../../auth/useAuthContext";

const MotionIcon = motion(Icon);

type notification = {
  _id: string;
  receiver: string;
  title: string;
  body: string;
  data: {
    invoiceRequestId: string;
    connectionRequestId: string;
    invoiceRequest: any;
  };
  read: boolean;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  __v: 0;
};

export default function NotificationBell({
  notification,
  markAsOpen,
  markAllAsRead,
  moreNotification,
  isMorePages,
}: {
  notification: notification[];
  markAsOpen: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  moreNotification: () => void;
  isMorePages: boolean;
}) {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const controls = useAnimation();

  const handleOpen = (e: any) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    getNotificationsCount();

    return () => {
      markAllAsRead();
    };
  }, []);

  const getNotificationsCount = async () => {
    try {
      const Response = await fetcher.get(
        END_POINTS.DASHBOARD.NOTIFICATION.UNREAD_COUNT
      );
      setNotificationCount(Response.data.count?.count);
    } catch (error) {}
  };

  useEffect(() => {
    if (!!notificationCount) {
      const interval = setInterval(() => {
        controls.start({
          rotate: [0, -10, 10, -6, 6, -2, 2, 0],
          transition: { duration: 0.5 },
        });
      }, 1000); // vibrate every 1 minute

      return () => clearInterval(interval);
    }
  }, [controls, notificationCount]);

  onMessage(messaging, (payload) => {
    console.log("Message received in foreground: ", payload);
    getNotificationsCount();
    // Show in-app toast or update UI
  });

  const handleNavigation = async (data: notification) => {
    await markAsOpen(data._id);
    await getNotificationsCount();
    if (data.type == "invoice_request") {
      console.log(data.data.invoiceRequest?.status);
      const status = data.data.invoiceRequest?.status;
      if (status == "pending") {
        user?.business?._id == data.data.invoiceRequest?.seller?._id
          ? navigate(PATH_DASHBOARD.home.pendingReceivables.view, {
              state: { invoiceData: data.data.invoiceRequest },
            })
          : navigate(
              PATH_DASHBOARD.home.pendingPayables.view(
                data.data.invoiceRequest?._id
              )
            );
      }
      if (status == "approved") {
        user?.business?._id == data.data.invoiceRequest?.seller?._id
          ? navigate(PATH_DASHBOARD.home.myReceivables.view, {
              state: data.data.invoiceRequest,
            })
          : navigate(PATH_DASHBOARD.home.myPayables.view, {
              state: data.data.invoiceRequest,
            });
      }
    }

    if (data.type == "connection") navigate(PATH_DASHBOARD.home.newConnections);
    if (data.type == "chat")
      navigate(PATH_DASHBOARD.Chat, { state: data.data.invoiceRequest });
  };

  return (
    <>
      <IconButton onClick={handleOpen} color="inherit">
        <Badge badgeContent={notificationCount} color="error">
          <MotionIcon
            icon="iconamoon:notification-light"
            width="24"
            height="24"
            animate={controls}
            color="primary.main"
            style={{
              color: theme.palette.primary.main,
            }}
          />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: 350, maxHeight: "calc(100vh - 200px)" } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack
          flexDirection={"row"}
          gap={1}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant="h4">Notifications</Typography>

          {!!notificationCount && (
            <Typography
              variant="caption"
              color="primary.main"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                markAllAsRead();
                setNotificationCount(0);
              }}
            >
              Mark all as read
            </Typography>
          )}
        </Stack>

        <Divider />

        {!!!notification.length && (
          <Typography variant="subtitle2" textAlign={"center"} py={3}>
            Notifications not found
          </Typography>
        )}
        {notification.map((notif) => (
          <Box
            sx={{
              width: "100%",
              px: 2,
              pt: 1,
              bgcolor: notif.read ? "" : theme.palette.grey[300],
            }}
            key={notif._id}
            onClick={handleClose}
          >
            <Typography variant="subtitle2">{notif.title}</Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {notif.body}
            </Typography>
            <Stack
              flexDirection={"row"}
              gap={1}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign={"end"}
              >
                {fDateTime(notif.createdAt)}
              </Typography>
              <Stack flexDirection={"row"} gap={1}>
                {/* {notif.status == "unopened" && ( */}
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleNavigation(notif)}
                >
                  Open
                </Typography>
                {/* )} */}
                {/* {!!!notif.read && notif.status == "unopened" && " | "}
                {!!!notif.read && (
                  <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ cursor: "pointer" }}
                    onClick={() => markAsOpen(notif._id)}
                  >
                    Mark as read
                  </Typography>
                )} */}
              </Stack>
            </Stack>
          </Box>
        ))}
        <Typography
          variant="subtitle2"
          color={isMorePages ? "primary.main" : "textDisabled"}
          textAlign={"center"}
          py={0.5}
          sx={{
            cursor: isMorePages ? "pointer" : "not-allowed",
            bgcolor: theme.palette.grey[200],
          }}
          onClick={(e) => {
            e.stopPropagation();
            isMorePages && moreNotification();
          }}
        >
          See more
        </Typography>
      </Menu>
    </>
  );
}
